import { useState, useEffect } from "react";
import { useLocalDraft } from "./useLocalDraft";
import { supabase } from "./supabaseclient";

const T = {
  bg: "#06090f", panel: "#0b0f1a", card: "#0f1623", border: "#1a2540",
  gold: "#c9a96e", goldLight: "#e8c98a", goldDim: "#7a6640",
  text: "#e8eaf0", textMid: "#7a8599", textDim: "#3d4a5c",
  active: "#10b981", activeBg: "#071f14", activeText: "#34d399",
  completedBg: "#13132e", completedText: "#a5b4fc", moderate: "#fbbf24",
};

// ── MRC GRADE OPTIONS ──────────────────────────────────────────────────────────
const MRC_OPTIONS = [
  { value: "", label: "Select grade..." },
  { value: "0", label: "0 — No contraction" },
  { value: "1", label: "1 — Flicker/trace contraction" },
  { value: "2", label: "2 — Movement, gravity eliminated" },
  { value: "3", label: "3 — Movement against gravity" },
  { value: "4-", label: "4− — Against slight resistance" },
  { value: "4", label: "4 — Against moderate resistance" },
  { value: "4+", label: "4+ — Against strong resistance" },
  { value: "5", label: "5 — Normal strength" },
];

// ── JOINT CONFIG — auto-detected from diagnosis ────────────────────────────────
const JOINT_CONFIGS = {
  knee: {
    label: "Knee",
    rom: [
      { id: "knee_flex", label: "Knee Flexion (°)", normal: "135", unit: "°" },
      { id: "knee_ext", label: "Knee Extension (°)", normal: "0", unit: "°" },
    ],
    mmt: [
      { id: "mmt_quad", label: "Quadriceps (Knee Extensors)" },
      { id: "mmt_hamstring", label: "Hamstrings (Knee Flexors)" },
      { id: "mmt_glut_med", label: "Gluteus Medius (Hip Abductors)" },
      { id: "mmt_glut_max", label: "Gluteus Maximus (Hip Extensors)" },
    ],
  },
  shoulder: {
    label: "Shoulder",
    rom: [
  { id: "sh_flex", label: "Shoulder Flexion (°)", normal: "180", unit: "°" },
  { id: "sh_ext", label: "Shoulder Extension (°)", normal: "60", unit: "°" },
  { id: "sh_abd", label: "Shoulder Abduction (°)", normal: "180", unit: "°" },
  { id: "sh_add", label: "Shoulder Adduction (°)", normal: "30", unit: "°" },
  { id: "sh_er", label: "External Rotation (°)", normal: "90", unit: "°" },
  { id: "sh_ir", label: "Internal Rotation (°)", normal: "70", unit: "°" },
],
    mmt: [
      { id: "mmt_deltoid", label: "Deltoid (Shoulder Flexors/Abductors)" },
    { id: "mmt_supra", label: "Supraspinatus" },
    { id: "mmt_infra", label: "Infraspinatus / Teres Minor" },
    { id: "mmt_subscap", label: "Subscapularis" },
    { id: "mmt_serratus", label: "Serratus Anterior" },
    { id: "mmt_pec_major", label: "Pectoralis Major" },
    { id: "mmt_lats", label: "Latissimus Dorsi" },
    { id: "mmt_rhomboids", label: "Rhomboids" },
    ],
  },
  lumbar: {
    label: "Lumbar Spine",
    rom: [
      { id: "lx_flex", label: "Lumbar Flexion (°)", normal: "60", unit: "°" },
      { id: "lx_ext", label: "Lumbar Extension (°)", normal: "25", unit: "°" },
      { id: "lx_lat_r", label: "Lateral Flexion Right (°)", normal: "25", unit: "°" },
      { id: "lx_lat_l", label: "Lateral Flexion Left (°)", normal: "25", unit: "°" },
      { id: "slr", label: "SLR Angle (°)", normal: "80", unit: "°" },
    ],
    mmt: [
      { id: "mmt_core", label: "Core / Transversus Abdominis" },
      { id: "mmt_multifidus", label: "Multifidus (Lumbar Extensors)" },
      { id: "mmt_hip_flex", label: "Hip Flexors (Iliopsoas)" },
      { id: "mmt_glut_max", label: "Gluteus Maximus" },
    ],
  },
  cervical: {
    label: "Cervical Spine",
    rom: [
      { id: "cx_flex", label: "Cervical Flexion (°)", normal: "50", unit: "°" },
      { id: "cx_ext", label: "Cervical Extension (°)", normal: "60", unit: "°" },
      { id: "cx_rot_r", label: "Rotation Right (°)", normal: "80", unit: "°" },
      { id: "cx_rot_l", label: "Rotation Left (°)", normal: "80", unit: "°" },
      { id: "cx_lat_r", label: "Lateral Flexion Right (°)", normal: "45", unit: "°" },
      { id: "cx_lat_l", label: "Lateral Flexion Left (°)", normal: "45", unit: "°" },
    ],
    mmt: [
      { id: "mmt_dnf", label: "Deep Neck Flexors (Longus Colli/Capitis)" },
      { id: "mmt_upper_trap", label: "Upper Trapezius" },
      { id: "mmt_scm", label: "Sternocleidomastoid" },
      { id: "mmt_lower_trap", label: "Lower Trapezius" },
    ],
  },
  ankle: {
    label: "Ankle & Foot",
    rom: [
      { id: "ank_df", label: "Ankle Dorsiflexion (°)", normal: "20", unit: "°" },
      { id: "ank_pf", label: "Ankle Plantarflexion (°)", normal: "50", unit: "°" },
      { id: "ank_inv", label: "Inversion (°)", normal: "35", unit: "°" },
      { id: "ank_ev", label: "Eversion (°)", normal: "25", unit: "°" },
    ],
    mmt: [
      { id: "mmt_gastroc", label: "Gastrocnemius/Soleus (Plantarflexors)" },
      { id: "mmt_tib_ant", label: "Tibialis Anterior (Dorsiflexors)" },
      { id: "mmt_peroneals", label: "Peroneals (Evertors)" },
      { id: "mmt_intrinsic", label: "Intrinsic Foot Muscles" },
    ],
  },
  hip: {
    label: "Hip",
    rom: [
      { id: "hip_flex", label: "Hip Flexion (°)", normal: "120", unit: "°" },
      { id: "hip_ext", label: "Hip Extension (°)", normal: "30", unit: "°" },
      { id: "hip_abd", label: "Hip Abduction (°)", normal: "45", unit: "°" },
      { id: "hip_add", label: "Hip Adduction (°)", normal: "30", unit: "°" },
      { id: "hip_er", label: "Hip External Rotation (°)", normal: "45", unit: "°" },
      { id: "hip_ir", label: "Hip Internal Rotation (°)", normal: "45", unit: "°" },
    ],
    mmt: [
      { id: "mmt_glut_max", label: "Gluteus Maximus (Hip Extensors)" },
      { id: "mmt_glut_med", label: "Gluteus Medius (Hip Abductors)" },
      { id: "mmt_hip_flex", label: "Iliopsoas (Hip Flexors)" },
      { id: "mmt_adductors", label: "Hip Adductors" },
      { id: "mmt_hip_er", label: "Hip External Rotators (Deep 6)" },
    ],
  },
  elbow: {
  label: "Elbow / Forearm / Wrist-Hand",
  rom: [
    { id: "elb_flex", label: "Elbow Flexion (°)", normal: "145", unit: "°" },
    { id: "elb_ext", label: "Elbow Extension (°)", normal: "0", unit: "°" },
    { id: "fore_sup", label: "Forearm Supination (°)", normal: "90", unit: "°" },
    { id: "fore_pro", label: "Forearm Pronation (°)", normal: "90", unit: "°" },
    { id: "wrist_ext", label: "Wrist Extension (°)", normal: "70", unit: "°" },
    { id: "wrist_flex", label: "Wrist Flexion (°)", normal: "80", unit: "°" },
    { id: "rad_dev", label: "Radial Deviation (°)", normal: "20", unit: "°" },
    { id: "uln_dev", label: "Ulnar Deviation (°)", normal: "30", unit: "°" },
    { id: "thumb_abd", label: "Thumb Abduction (°)", normal: "60", unit: "°" },
    { id: "grip_dyn", label: "Grip Strength", normal: "Functional", unit: "" },
  ],
  mmt: [
    { id: "mmt_biceps", label: "Biceps Brachii (Elbow Flexors)" },
    { id: "mmt_triceps", label: "Triceps (Elbow Extensors)" },
    { id: "mmt_wrist_ext", label: "Wrist Extensors" },
    { id: "mmt_wrist_flex", label: "Wrist Flexors" },
    { id: "mmt_grip", label: "Grip Strength" },
    { id: "mmt_thumb", label: "Thumb Abductors / Extensors" },
    { id: "mmt_intrinsic_hand", label: "Intrinsic Hand Muscles" },
  ],
},
};

// ALL EXTRA MUSCLE GROUPS for manual addition
const ALL_MUSCLE_GROUPS = [
  { id: "mmt_quad", label: "Quadriceps (Knee Extensors)" },
  { id: "mmt_hamstring", label: "Hamstrings (Knee Flexors)" },
  { id: "mmt_glut_max", label: "Gluteus Maximus (Hip Extensors)" },
  { id: "mmt_glut_med", label: "Gluteus Medius (Hip Abductors)" },
  { id: "mmt_adductors", label: "Hip Adductors" },
  { id: "mmt_hip_flex", label: "Iliopsoas (Hip Flexors)" },
  { id: "mmt_hip_er", label: "Hip External Rotators" },
  { id: "mmt_gastroc", label: "Gastrocnemius/Soleus" },
  { id: "mmt_tib_ant", label: "Tibialis Anterior" },
  { id: "mmt_peroneals", label: "Peroneals" },
  { id: "mmt_intrinsic", label: "Intrinsic Foot Muscles" },
  { id: "mmt_supra", label: "Supraspinatus" },
  { id: "mmt_infra", label: "Infraspinatus/Teres Minor" },
  { id: "mmt_subscap", label: "Subscapularis" },
  { id: "mmt_deltoid", label: "Deltoid" },
  { id: "mmt_serratus", label: "Serratus Anterior" },
  { id: "mmt_core", label: "Core / Transversus Abdominis" },
  { id: "mmt_multifidus", label: "Multifidus" },
  { id: "mmt_dnf", label: "Deep Neck Flexors" },
  { id: "mmt_upper_trap", label: "Upper Trapezius" },
  { id: "mmt_lower_trap", label: "Lower Trapezius" },
  { id: "mmt_biceps", label: "Biceps Brachii" },
  { id: "mmt_triceps", label: "Triceps" },
  { id: "mmt_wrist_ext", label: "Wrist Extensors" },
  { id: "mmt_wrist_flex", label: "Wrist Flexors" },
  { id: "mmt_grip", label: "Grip Strength" },
  { id: "mmt_scm", label: "Sternocleidomastoid" },
  { id: "mmt_rhomboids", label: "Rhomboids" },
  { id: "mmt_lats", label: "Latissimus Dorsi" },
  { id: "mmt_pec_major", label: "Pectoralis Major" },
];

// ── DETECT JOINT FROM DIAGNOSIS ────────────────────────────────────────────────
function normalizeJointKey(joint) {
  if (!joint) return "";

  const map = {
    shoulder: "shoulder",
    elbow: "elbow",
    wrist_hand: "elbow",
    cervical_spine: "cervical",
    thoracic_spine: "cervical",
    lumbar_spine: "lumbar",
    hip: "hip",
    knee: "knee",
    ankle_foot: "ankle",
    neuro: "lumbar",
    other: "",
  };

  return map[joint] || "";
}
function getDisplayCondition(patient) {
  if (patient?.condition === "Other" && patient?.customCondition) {
    return patient.customCondition;
  }
  return patient?.condition || "";
}
function detectJoint(condition) {
  const c = (condition || "").toLowerCase();

  if (
    c.includes("knee") ||
    c.includes("acl") ||
    c.includes("pcl") ||
    c.includes("meniscus") ||
    c.includes("osteoarthritis") ||
    c.includes("patell")
  ) return "knee";

  if (
    c.includes("shoulder") ||
    c.includes("rotator") ||
    c.includes("slap") ||
    c.includes("impingement") ||
    c.includes("frozen") ||
    c.includes("periarthritis")
  ) return "shoulder";

  if (
    c.includes("lumbar") ||
    c.includes("disc") ||
    c.includes("back") ||
    c.includes("spondylosis") ||
    c.includes("radiculopathy") ||
    c.includes("sciatica")
  ) return "lumbar";

  if (
    c.includes("cervical") ||
    c.includes("neck") ||
    c.includes("whiplash")
  ) return "cervical";

  if (
    c.includes("ankle") ||
    c.includes("plantar") ||
    c.includes("achilles") ||
    c.includes("foot") ||
    c.includes("heel")
  ) return "ankle";

  if (
    c.includes("hip") ||
    c.includes("fai") ||
    c.includes("trochant") ||
    c.includes("piriformis")
  ) return "hip";

  if (
    c.includes("elbow") ||
    c.includes("epicondyl") ||
    c.includes("tennis") ||
    c.includes("golfer") ||
    c.includes("wrist") ||
    c.includes("carpal") ||
    c.includes("de quervain") ||
    c.includes("dequervain") ||
    c.includes("thumb") ||
    c.includes("volkmann")
  ) return "elbow";

  return "lumbar";
}

// ── SOAP TEMPLATES ─────────────────────────────────────────────────────────────
function buildROMSummary(romValues, jointConfig) {
  if (!jointConfig || !romValues) return "";
  const entries = jointConfig.rom
    .filter(r => romValues[r.id])
    .map(r => `${r.label.replace(" (°)", "")}: ${romValues[r.id]}°`);
  return entries.length > 0 ? entries.join(", ") : "";
}

function buildMMTSummary(mmtValues, jointConfig, extraMmt) {
  if (!jointConfig) return "";
  const baseEntries = jointConfig.mmt
    .filter(m => mmtValues[m.id])
    .map(m => `${m.label.split("(")[0].trim()}: ${mmtValues[m.id]}/5`);
  const extraEntries = extraMmt
    .filter(m => mmtValues[m.id])
    .map(m => `${m.label.split("(")[0].trim()}: ${mmtValues[m.id]}/5`);
  return [...baseEntries, ...extraEntries].join(", ");
}

function generateSOAPNote(patient, customFields, romValues, mmtValues, extraMmt, sessionNum, effectiveJoint) {
  const painScore = patient.painScore || 5;
  const painDesc = painScore >= 7 ? "severe (NPRS " + painScore + "/10)" : painScore >= 4 ? "moderate (NPRS " + painScore + "/10)" : "mild (NPRS " + painScore + "/10)";
  const side = customFields.side || "affected";
  const phase = sessionNum <= 3 ? "acute" : sessionNum <= 8 ? "subacute" : "rehabilitation";
 const jointKey = effectiveJoint || detectJoint(patient.condition);
  const jointConfig = JOINT_CONFIGS[jointKey];
  const jointLabel = jointConfig?.label || "affected region";
  const romSummary = buildROMSummary(romValues, jointConfig);
  const mmtSummary = buildMMTSummary(mmtValues, jointConfig, extraMmt);
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  // Build weakest muscle for plan focus
  const allMmtEntries = [...(jointConfig?.mmt || []), ...extraMmt].filter(m => mmtValues[m.id]);
  const weakestMuscle = allMmtEntries.find(m => ["0","1","2","3"].includes(mmtValues[m.id]));
  const strengthTarget = weakestMuscle ? weakestMuscle.label.split("(")[0].trim() : "primary movers";

  const subjective = `Patient presents with ${painDesc} ${side} ${jointLabel} pain on ${today}, Session #${sessionNum}. ${customFields.aggravating ? `Pain aggravated by ${customFields.aggravating}.` : "Symptoms aggravated by functional activities."} ${customFields.relief ? `Partially relieved by ${customFields.relief}.` : "Some relief with rest."} ${customFields.function_limit ? `Functional limitation: ${customFields.function_limit}.` : `Functional limitations noted in daily activities.`} ${customFields.notes ? `Patient reports: ${customFields.notes}` : ""}`.trim();

  const objective = `Diagnosis: ${getDisplayCondition(patient)}. ${romSummary ? `ROM — ${romSummary}.` : `${jointLabel} ROM assessed and documented.`} ${mmtSummary ? `MMT — ${mmtSummary}.` : `Muscle strength assessed using MRC grading.`} ${painScore >= 7 ? "Significant pain inhibition noted affecting muscle performance." : painScore >= 4 ? "Moderate pain present, functional movement analysis performed." : "Pain well-controlled, full assessment completed."} Posture and movement quality assessed. Special tests performed as clinically indicated.`;

  const assessment = `${getDisplayCondition(patient)} with ${painDesc}. Currently in ${phase} phase of rehabilitation (Session ${sessionNum}/${patient.totalSessions}). ${mmtSummary ? `Muscle strength deficits identified: ${mmtSummary}.` : ""} ${romSummary ? `ROM restrictions: ${romSummary}.` : ""} ${painScore >= 7 ? "High pain severity limiting full participation in rehabilitation. Pain management prioritised this session." : painScore >= 4 ? "Moderate disability consistent with expected presentation. Progressing as planned." : "Good progress noted. Functional recovery on track."} Prognosis ${painScore <= 4 ? "good" : "fair to good"} with continued structured physiotherapy.`;

  const plan = buildEvidencePlan(getDisplayCondition(patient), jointKey, strengthTarget, painScore, sessionNum, patient.totalSessions);

  return { subjective, objective, assessment, plan };
}

function buildEvidencePlan(condition, jointKey, strengthTarget, painScore, sessionNum, totalSessions) {
  const remaining = totalSessions - sessionNum;
  const review = Math.min(3, Math.ceil(remaining / 3));
  const phase = sessionNum <= 3 ? "acute" : sessionNum <= 8 ? "subacute" : "rehabilitation";

  const plans = {
    knee: `1) ${strengthTarget} strengthening: isometric to isotonic progression — 3×15 reps, daily (Cochrane 2015 Grade A). 2) Closed kinetic chain exercises: mini squats, leg press, step-ups — 3×12 reps. 3) Proprioception: single-leg stance, wobble board progressions. 4) Gait retraining and functional movement training. 5) HEP: quad sets, SLR, heel slides. Review in ${review} sessions.`,
    shoulder: `1) ${strengthTarget} isometric exercises (pain relief phase) progressing to isotonic — 3×15 reps (Ainsworth et al., BJSM 2007, Grade A). 2) Scapular stabilization: serratus anterior, lower trapezius activation — Y/T/W exercises. 3) ROM: Codman's pendulum, pulley exercises, wand exercises. 4) Postural correction: scapular retraction, chin tuck. 5) HEP: theraband ER/IR, wall slides. Review in ${review} sessions.`,
    lumbar: `1) Core stabilization: transversus abdominis activation, dead bug, bird-dog — 3×10 reps (Richardson et al., Grade A). 2) ${phase === "acute" ? "McKenzie extension exercises for centralization (Grade A evidence)" : "Progressive functional movement: Romanian deadlift, squat pattern with neutral spine"}. 3) Neural mobilization: nerve sliders if radicular symptoms present. 4) Education: posture correction, lifting mechanics, activity pacing. 5) HEP: pelvic tilts, knee-to-chest, cat-camel. Review in ${review} sessions.`,
    cervical: `1) Deep cervical flexor training: craniocervical flexion exercise — 10×10 sec holds (Grade A). 2) ${strengthTarget} strengthening — isometric progressions. 3) Cervical joint mobilization: grade III–IV as tolerated. 4) Postural correction: scapular retraction, thoracic extension. 5) Ergonomic advice, workstation assessment. HEP: chin tucks, upper trap stretch, levator scapulae stretch. Review in ${review} sessions.`,
    ankle: `1) ${strengthTarget} strengthening: eccentric heel raises — 3×15 reps (Alfredson protocol, Grade A). 2) Plantar fascia-specific stretching: 3×10 reps, 3×/day (DiGiovanni et al., JBJS 2003, Grade A). 3) Intrinsic foot strengthening: short foot exercise, toe spreads. 4) Balance and proprioception: single-leg stance, BOSU progressions. 5) HEP: calf stretching, ice massage post-activity. Review in ${review} sessions.`,
    hip: `1) ${strengthTarget} strengthening: progressive resistance — 3×15 reps, 3×/week. 2) Gluteal strengthening: bridges, clamshells, lateral band walks — 3×15. 3) Functional training: sit-to-stand, step-ups, single-leg squat progression. 4) Stretching: hip flexors, piriformis, IT band. 5) Gait retraining, activity modification advice. HEP compliance reviewed. Review in ${review} sessions.`,
    elbow: `1) ${strengthTarget} eccentric loading (heavy slow resistance) — 3×15 progressing to 3×6 reps (Beyer et al., Am J Sports Med 2015, Grade A). 2) Wrist extensor/flexor strengthening: theraband, progressive resistance. 3) Grip strengthening progression. 4) Technique modification if sport/occupation related. 5) Counterforce brace education. HEP: eccentric wrist exercises, stretching. Review in ${review} sessions.`,
  };

  return plans[jointKey] || plans["lumbar"];
}

const SOAP_SECTIONS = [
  { key: "subjective", label: "S — Subjective", icon: "💬", color: "#22d3ee", desc: "Patient's complaints & history" },
  { key: "objective", label: "O — Objective", icon: "🔍", color: T.gold, desc: "Clinical findings & measurements" },
  { key: "assessment", label: "A — Assessment", icon: "🧠", color: "#a78bfa", desc: "Diagnosis & interpretation" },
  { key: "plan", label: "P — Plan", icon: "📋", color: T.activeText, desc: "Evidence-based treatment plan" },
];

export default function SOAPNoteAI({ patient, session, notify, effectiveJoint }) {
  const sessionNum = (patient?.sessions || 0) + 1;
  const jointKey =
  normalizeJointKey(effectiveJoint) ||
  detectJoint(patient.customCondition || patient.condition);
  const jointConfig = JOINT_CONFIGS[jointKey];

const [customFields, setCustomFields, clearCustom] = useLocalDraft(`soap_custom_${patient.id}`, { notes: "", side: "", aggravating: "", relief: "", function_limit: "" });
const [romValues, setRomValues, clearRom] = useLocalDraft(`soap_rom_${patient.id}`, {});
const [mmtValues, setMmtValues, clearMmt] = useLocalDraft(`soap_mmt_${patient.id}`, {});
  const [extraMmt, setExtraMmt] = useState([]);
  const [showAddMuscle, setShowAddMuscle] = useState(false);
  const [selectedExtraMuscle, setSelectedExtraMuscle] = useState("");

  const [soap, setSoap] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedNotes, setSavedNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [view, setView] = useState("generate");
  const [editMode, setEditMode] = useState(false);
  const [editedSoap, setEditedSoap] = useState(null);

  function generateNote() {
    const generated = generateSOAPNote(
  patient,
  customFields,
  romValues,
  mmtValues,
  extraMmt,
  sessionNum,
  effectiveJoint
);
    setSoap(generated);
    setEditedSoap({ ...generated });
    setEditMode(false);
  }

  function addExtraMuscle() {
    if (!selectedExtraMuscle) return;
    const muscle = ALL_MUSCLE_GROUPS.find(m => m.id === selectedExtraMuscle);
    if (!muscle || extraMmt.find(m => m.id === muscle.id)) return;
    setExtraMmt(prev => [...prev, muscle]);
    setSelectedExtraMuscle("");
    setShowAddMuscle(false);
  }

  function removeExtraMuscle(id) {
    setExtraMmt(prev => prev.filter(m => m.id !== id));
    setMmtValues(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  async function saveSOAPNote() {
    if (!soap) return;
    setSaving(true);
    const finalSoap = editMode ? editedSoap : soap;
    const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    const noteText = `SOAP NOTE — Session #${sessionNum} · ${today}\n\nSUBJECTIVE:\n${finalSoap.subjective}\n\nOBJECTIVE:\n${finalSoap.objective}\n\nASSESSMENT:\n${finalSoap.assessment}\n\nPLAN:\n${finalSoap.plan}\n\n[Evidence-based clinical documentation]`;

    const { error } = await supabase.from("patients").update({
      notes: noteText,
    }).eq("id", patient.id);

    if (error) { notify("Save failed: " + error.message, "error"); setSaving(false); return; }
    notify("SOAP note saved to Notes ✓");
    clearCustom(); clearRom(); clearMmt();
    setSaving(false);
    setSoap(null);
    setEditMode(false);
    setCustomFields({ notes: "", side: "", aggravating: "", relief: "", function_limit: "" });
    setRomValues({});
    setMmtValues({});
    setExtraMmt([]);
  }

  async function fetchSavedNotes() {
    setLoadingNotes(true);
    const { data, error } = await supabase.from("patients").select("notes, history").eq("id", patient.id).single();
    if (!error && data) {
      const notes = [];
      if (data.notes && data.notes.includes("SOAP NOTE")) notes.push(data.notes);
      const historyNotes = (data.history || []).filter(h => h.includes("SOAP NOTE"));
      setSavedNotes([...notes, ...historyNotes]);
    }
    setLoadingNotes(false);
  }

  // Filter out already-added muscles from dropdown
  const availableMuscles = ALL_MUSCLE_GROUPS.filter(m =>
    !extraMmt.find(e => e.id === m.id) &&
    !jointConfig?.mmt.find(e => e.id === m.id)
  );

  return (
    <div>
      {/* Tab selector */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
        {[
          { id: "generate", label: "📝 Generate SOAP Note" },
          { id: "history", label: "📋 Saved Notes" },
        ].map((t) => (
          <button key={t.id} className="btn"
            onClick={() => { setView(t.id); if (t.id === "history") fetchSavedNotes(); }}
            style={{ padding: "9px 20px", fontSize: 12, fontWeight: 600, background: view === t.id ? "rgba(201,169,110,0.12)" : "transparent", border: `1px solid ${view === t.id ? T.gold : T.border}`, color: view === t.id ? T.gold : T.textMid, borderRadius: 8 }}>
            {t.label}
          </button>
        ))}
      </div>

      {view === "generate" && (
        <div>
          {/* Patient Context Banner */}
          <div style={{ background: "rgba(201,169,110,0.06)", border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 18px", marginBottom: 20, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
            {[
              { label: "Patient", val: patient.name },
              { label: "Diagnosis", val: patient.condition },
              { label: "NPRS", val: `${patient.painScore}/10`, color: T.moderate },
              { label: "Session", val: `#${sessionNum}`, color: T.gold },
              { label: "Joint / Region", val: jointConfig?.label || "General", color: T.activeText },
            ].map((f) => (
              <div key={f.label}>
                <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.6px" }}>{f.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: f.color || T.text, marginTop: 2 }}>{f.val}</div>
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 24, marginBottom: 20 }}>
            {/* General Fields */}
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 13 }}>General Clinical Information</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>Side Affected</label>
                <select value={customFields.side} onChange={(e) => setCustomFields(f => ({ ...f, side: e.target.value }))}>
                  <option value="">Auto-detect</option>
                  <option value="right">Right</option>
                  <option value="left">Left</option>
                  <option value="bilateral">Bilateral</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>Aggravating Factors</label>
                <input value={customFields.aggravating} onChange={(e) => setCustomFields(f => ({ ...f, aggravating: e.target.value }))} placeholder="e.g. prolonged sitting, stairs" />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>Relieving Factors</label>
                <input value={customFields.relief} onChange={(e) => setCustomFields(f => ({ ...f, relief: e.target.value }))} placeholder="e.g. rest, ice, heat" />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>Functional Limitation</label>
                <input value={customFields.function_limit} onChange={(e) => setCustomFields(f => ({ ...f, function_limit: e.target.value }))} placeholder="e.g. walking, climbing stairs" />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>Additional Clinical Notes</label>
                <textarea rows={2} value={customFields.notes} onChange={(e) => setCustomFields(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional observations, patient feedback, special test findings..." style={{ resize: "vertical" }} />
              </div>
            </div>

            {/* ROM Fields — Joint Specific */}
            {jointConfig && (
              <>
                <div style={{ height: 1, background: T.border, margin: "16px 0" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 13 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim }}>
                    {jointConfig.label} — Range of Motion
                  </div>
                  <span style={{ fontSize: 10, color: T.textDim }}>(leave blank to auto-fill)</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
                  {jointConfig.rom.map((r) => (
                    <div key={r.id}>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>
                        {r.label} <span style={{ color: T.textDim, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>({r.normal}° normal)</span>
                      </label>
                      <input
                        type="number"
                        value={romValues[r.id] || ""}
                        onChange={(e) => setRomValues(v => ({ ...v, [r.id]: e.target.value }))}
                        placeholder={`Normal: ${r.normal}°`}
                        min={0} max={360}
                      />
                    </div>
                  ))}
                </div>

                {/* MMT Fields — Joint Specific */}
                <div style={{ height: 1, background: T.border, margin: "16px 0" }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 13 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim }}>
                      {jointConfig.label} — Manual Muscle Testing (MRC Grade)
                    </div>
                    <div style={{ fontSize: 10, color: T.textDim, marginTop: 3 }}>Primary muscle groups for {jointConfig.label}</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  {jointConfig.mmt.map((m) => (
                    <div key={m.id}>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>{m.label}</label>
                      <select value={mmtValues[m.id] || ""} onChange={(e) => setMmtValues(v => ({ ...v, [m.id]: e.target.value }))}>
                        {MRC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Extra Muscle Groups */}
                {extraMmt.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                    {extraMmt.map((m) => (
                      <div key={m.id} style={{ position: "relative" }}>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.7px", textTransform: "uppercase", color: T.gold, marginBottom: 5 }}>
                          {m.label}
                          <button onClick={() => removeExtraMuscle(m.id)} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 12, marginLeft: 8, padding: 0 }}>✕</button>
                        </label>
                        <select value={mmtValues[m.id] || ""} onChange={(e) => setMmtValues(v => ({ ...v, [m.id]: e.target.value }))}>
                          {MRC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Extra Muscle Button */}
                {!showAddMuscle ? (
                  <button className="btn btn-outline btn-sm" onClick={() => setShowAddMuscle(true)} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    + Add Additional Muscle Group
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <select value={selectedExtraMuscle} onChange={(e) => setSelectedExtraMuscle(e.target.value)} style={{ flex: 1 }}>
                      <option value="">Select muscle group...</option>
                      {availableMuscles.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                    <button className="btn btn-gold btn-sm" onClick={addExtraMuscle}>Add</button>
                    <button className="btn btn-outline btn-sm" onClick={() => { setShowAddMuscle(false); setSelectedExtraMuscle(""); }}>Cancel</button>
                  </div>
                )}
              </>
            )}

            <div style={{ height: 1, background: T.border, margin: "20px 0" }} />
            <button className="btn btn-gold" onClick={generateNote} style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 24px" }}>
              📝 Generate SOAP Note
            </button>
          </div>

          {/* Generated SOAP */}
          {soap && (
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 600, color: T.goldLight }}>Generated SOAP Note</div>
                  <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>Evidence-based · Review and edit before saving</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btn-outline btn-sm" onClick={generateNote}>🔄 Regenerate</button>
                  <button className="btn btn-outline btn-sm" onClick={() => setEditMode(!editMode)}>{editMode ? "👁 Preview" : "✎ Edit"}</button>
                  <button className="btn btn-gold btn-sm" onClick={saveSOAPNote} disabled={saving}>{saving ? "Saving..." : "💾 Save to Notes"}</button>
                </div>
              </div>

              <div style={{ display: "grid", gap: 14 }}>
                {SOAP_SECTIONS.map((section) => (
                  <div key={section.key} style={{ background: T.panel, borderRadius: 10, padding: 18, borderLeft: `3px solid ${section.color}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ fontSize: 16 }}>{section.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: section.color }}>{section.label}</div>
                        <div style={{ fontSize: 10, color: T.textDim }}>{section.desc}</div>
                      </div>
                    </div>
                    {editMode ? (
                      <textarea rows={4} value={editedSoap?.[section.key] || ""} onChange={(e) => setEditedSoap((s) => ({ ...s, [section.key]: e.target.value }))} style={{ resize: "vertical", fontSize: 13 }} />
                    ) : (
                      <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.8 }}>{soap[section.key]}</div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16, padding: "10px 14px", background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.2)", borderRadius: 8, fontSize: 11, color: T.textDim }}>
                ✓ This SOAP note will be saved to the patient's <strong style={{ color: T.activeText }}>Notes</strong> section with a timestamp. Always review before saving.
              </div>
            </div>
          )}
        </div>
      )}

      {view === "history" && (
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px", textTransform: "uppercase", color: T.goldDim, marginBottom: 13 }}>Saved SOAP Notes</div>
          {loadingNotes ? (
            <div style={{ color: T.textDim, fontSize: 13 }}>Loading...</div>
          ) : savedNotes.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: T.goldLight, marginBottom: 8 }}>No SOAP Notes Yet</div>
              <div style={{ fontSize: 13, color: T.textDim }}>Generate and save your first SOAP note above.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {savedNotes.map((note, i) => (
                <div key={i} style={{ background: T.panel, borderRadius: 10, padding: 18, border: `1px solid ${T.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.goldLight }}>{note.split("\n")[0]}</div>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "rgba(201,169,110,0.1)", color: T.gold, fontWeight: 700 }}>Evidence-Based</span>
                  </div>
                  <pre style={{ fontSize: 12, color: T.textMid, lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                    {note.split("\n").slice(2).join("\n")}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}